export const SECTION_NAME = "Rental";

export const INTRO_TEXT = `Rental transactions record the renting of an
  inventory item by a customer.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "12345",
    "date": "2019-05-08T09:13:00",
    "customer": {
      "id": 23,
      "name": "Bob's Bikes"
    },
    "fromLocation": {
      "id": 1,
      "name": "Warehouse"
    },
    "toLocation": {
      "id": 6,
      "name": "Out for Rental"
    },
    "billing": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "shipping": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "channel": {
      "id": 3,
      "name": "Catalog"
    },
    "department": null,
    "linkedTransaction": null,
    "customerMessage": null,
    "comment": null,
    "customerNotes": "",
    "total": 12.0,
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "closed": true,
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": {
          "id": 19,
          "name": "Class 1"
        },
        "job": {
          "id": 54,
          "name": "2018-1834"
        },
        "workcenter": {
          "id": 13,
          "name": "Final Inspection"
        },
        "tax": {
          "taxable": false,
          "taxCode": null
        },
        "linkedTransaction": null,
        "description": null,
        "quantity": 1.00000,
        "weight": 12.0000000000,
        "volume": 12.0000000000,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12.00000,
        "amount": 12.00000,
        "altAmount": 0,
        "picked": 0.00000,
        "shipped": 1.00000,
        "invoiced": 1.00000,
        "produced": 0,
        "returned": 0,
        "cost": 1.00000,
        "margin": 0.11000,
        "listprice": 20.00000,
        "percentdiscount": 0,
        "duedate": "2019-05-08T00:00:00",
        "uom": {
          "id": 2,
          "name": "doz"
        },
        "bin": null,
        "lot": null,
        "serials": [],
        "toBin": null
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a sales order, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "createCustomerIfNotFound",
    type: "boolean",
    description:
      "If true, and the customer is not found, this option will create a new customer record. Only valid on POST.",
  },
  {
    name: "fromLocation",
    type: "reference",
    description: `The name of the originating location.`,
  },
  {
    name: "toLocation",
    type: "reference",
    description: `The name of the return destination.`,
  },
  {
    name: "billing",
    type: "transaction address",
    description: `Billing address.`,
  },
  {
    name: "shipping",
    type: "transaction address",
    description: `Shipping address.`,
  },
  {
    name: "terms",
    type: "reference",
    description: `Payment terms.`,
  },
  {
    name: "salesRep",
    type: "reference",
    description: `Sales representative for this transaction.`,
  },
  {
    name: "channel",
    type: "reference",
    description: `Channel (e.g., Catalog, Retail Store) for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "priority",
    type: "reference",
    description: `The degree of importance or urgency assigned to this transaction.`,
  },
  {
    name: "assignedToUser",
    type: "reference",
    description: `User this transaction is assigned to.`,
  },
  {
    name: "orderStage",
    type: "reference",
    description: `The order stage of this transaction.`,
  },
  {
    name: "taxCode",
    type: "reference",
    description: `Tax code for transaction.`,
  },
  {
    name: "currency",
    type: "reference",
    description: `Currency used for transaction if multicurrency is enabled`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this rental.`,
  },
  {
    name: "exchangeRate",
    type: "decimal",
    description: `The exchange rate used for this transaction, if multicurrency is enabled.`,
  },
  {
    name: "customerMessage",
    type: "string",
    description: `Customer message field.`,
  },
  {
    name: "statusMessage",
    type: "string",
    description: `Status message field.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "customerPO",
    type: "string",
    description: `Customer purchase order field.`,
  },
  {
    name: "depositPercent",
    type: "decimal",
    description: `Deposit percentage field.`,
  },
  {
    name: "depositAmount",
    type: "decimal",
    description: `Deposit amount field.`,
  },
  {
    name: "subtotal",
    type: "decimal",
    description: `Subtotal for transaction.`,
    readOnly: true,
  },
  {
    name: "discountPercent",
    type: "decimal",
    description: `Discount percentage field.`,
  },
  {
    name: "discountAmount",
    type: "decimal",
    description: `Discount amount field.`,
  },
  {
    name: "taxPercent",
    type: "decimal",
    description: `Tax percentage field.`,
  },
  {
    name: "taxAmount",
    type: "decimal",
    description: `Tax amount field.`,
  },
  {
    name: "shippingAmount",
    type: "decimal",
    description: `Shipping amount field.`,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "discountTaxable",
    type: "boolean",
    description: `True if discount is taxable, false if not.`,
  },
  {
    name: "shippingTaxable",
    type: "boolean",
    description: `True if shipping is taxable, false if not.`,
  },
  {
    name: "dropship",
    type: "boolean",
    description: `True if order is to be drop shipped, false if not.`,
  },
  {
    name: "closed",
    type: "boolean",
    description: `True if transaction is closed, false if not.`,
    readOnly: true,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "statusLink",
    type: "string",
    description: `Link to OrderFacts page for this transaction, if enabled.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of rental line items",
    description: `The lines for the rental.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this rental line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the rental transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "tax",
    type: "tax information object",
    description: `The tax information for this line, if enabled.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity for this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitPrice",
    type: "decimal",
    description: `The unit price for this item.  Must be above the item’s minimum price, if set.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The rental amount for this line. The amount must equal the quantity multiplied by the unit price.`,
  },
  {
    name: "altAmount",
    type: "",
    description: "Unused.",
  },
  {
    name: "picked",
    type: "decimal",
    description: `The number of items picked on this line.`,
  },
  {
    name: "shipped",
    type: "decimal",
    description: `The number of items shipped on this line.`,
  },
  {
    name: "invoiced",
    type: "decimal",
    description: `the number of items invoiced on this line`,
  },
  {
    name: "produced",
    type: "",
    description: "Unused.",
  },
  {
    name: "returned",
    type: "",
    description: "Unused.",
  },
  {
    name: "cost",
    type: "decimal",
    description: `The cost for this line.`,
  },
  {
    name: "margin",
    type: "decimal",
    description: `The margin for this line.`,
  },
  {
    name: "listPrice",
    type: "decimal",
    description: `The list price for this item.`,
  },
  {
    name: "percentDiscount",
    type: "decimal",
    description: `The discount percentage applied to this line.`,
  },
  {
    name: "dueDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `The due date for this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `Bin from which this item was shipped.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `Lot for this line item.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `Serial numbers for this line item.`,
  },
  {
    name: "toBin",
    type: "reference",
    description: `Bin to which this item was returned.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "status",
    type: "string: open/closed",
    description: `Filters results by whether the transaction is open or closed.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, or customer name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "fromLocation",
    type: "string",
    description: `Filters rental records according to the name of the originating location.`,
  },
  {
    name: "toLocation",
    type: "string",
    description: `Filters rental records according to the name of the destination location.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/rental?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&fromLocation=warehouse&toLocation=Out%20for%20Rental&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
